<i18n lang="yaml" src="@/locales/common.yaml"></i18n>

<i18n lang="yaml">
en:
  select_date_range: Select date range
  tooltip: Click to change the global date range
cs:
  select_date_range: Vyberte rozmezí dat
  tooltip: Kliknutím změníte globální nastavení zobrazovaného rozmezí dat
</i18n>

<template>
  <div
    id="date-range-selector"
    :class="$vuetify.display.smAndDown ? 'pl-0' : ''"
    :style="
      highlight
        ? {
            'background-color': 'rgba(var(--v-theme-warning), 0.15)',
            border: 'solid 4px rgb(var(--v-theme-warning))',
          }
        : { border: 'solid 4px transparent' }
    "
  >
    <v-tooltip location="bottom" :disabled="!compactHeader">
      <template #activator="{ props }">
        <v-select
          v-model="selectedRangeName"
          v-bind="props"
          :items="dateRanges"
          item-title="nameLocal"
          item-value="name"
          :label="compactHeader ? '' : $t('labels.date_range')"
          hide-details
          :max-width="compactHeader ? '340' : '500'"
          :class="[
            $vuetify.display.smAndDown ? 'pa-4' : '',
            {
              'date-range--stacked': stacked && (hasDates || compactHeader),
              'mb-2': !datesOnly && stacked && hasDates,
            },
          ]"
        >
          <template #prepend-inner>
            <!-- no color: inherit the field text (dark top bar vs. nav-bar side panel) -->
            <v-icon :icon="$icons.calendar"></v-icon>
          </template>
          <template #selection>
            <div
              class="d-flex"
              :class="stacked ? 'flex-column' : 'align-baseline'"
            >
              <span v-if="datesOnly && !hasDates">{{ rangeName }}</span>
              <span v-else-if="!datesOnly"
                >{{ rangeName }}{{ stacked || !hasDates ? "" : ":" }}</span
              >
              <span
                v-if="hasDates"
                :class="[
                  stacked ? '' : 'ml-2',
                  datesOnly
                    ? ''
                    : [
                        'date-range__dates',
                        stacked ? 'text-body-small' : 'text-body-medium',
                      ],
                ]"
              >
                <DateRangeText :start="start" :end="end"></DateRangeText>
              </span>
            </div>
          </template>
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              @click="item.custom && (showDialog = true)"
            >
              <v-list-item-subtitle>
                <div
                  class="d-flex align-center"
                  :class="item.desc ? 'justify-space-between' : 'justify-end'"
                >
                  <span v-if="item.desc">{{ $t(item.desc) }}</span>
                  <DateRangeText
                    class="text-body-small"
                    v-if="item.start"
                    :start="item.start"
                    :end="item.end"
                  ></DateRangeText>
                </div>
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-select>
      </template>
      {{ $t("tooltip") }}
    </v-tooltip>
    <v-dialog v-model="showDialog" max-width="560px">
      <v-card>
        <v-card-title class="mt-2 mb-4"
          >{{ $t("select_date_range") }}:
          <DateRangeText :start="start" :end="end"></DateRangeText
        ></v-card-title>
        <v-divider></v-divider>
        <DateRangeSelector @close="showDialog = false"></DateRangeSelector>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DateRangeText from "@/components/util/DateRangeText";
import { mapActions, mapGetters, mapState } from "vuex";
import DateRangeSelector from "./DateRangeSelector";

// custom widths, tuned to the top bar content; Vuetify breakpoints (md 840 / lg 1145) don't fit it
const COMPACT_WIDTH = 1210;
const DATES_ONLY_WIDTH = 1030;

export default {
  name: "SelectedDateRangeWidget",
  components: { DateRangeText, DateRangeSelector },
  data() {
    return {
      showDialog: false,
    };
  },
  computed: {
    ...mapState({
      start: "dateRangeStart",
      end: "dateRangeEnd",
      highlight: "highlightDateRangeSelector",
      fiscalYearStart: "fiscalYearStart",
      dateRangeName: "dateRangeName",
      defaultDateRangeName: "defaultDateRangeName",
      defaultFYDateRangeName: "defaultFYDateRangeName",
    }),
    ...mapGetters({
      rangeObject: "selectedDateRange",
      dateRangesRaw: "dateRanges",
    }),
    dateRanges() {
      return this.dateRangesRaw.map((item) => ({
        ...item,
        nameLocal: this.$t(item.name),
      }));
    },
    selectedRangeName: {
      get() {
        return this.dateRangeName;
      },
      set(value) {
        this.changeDateRangeObject(value);
      },
    },
    compactHeader() {
      const { width, mdAndUp } = this.$vuetify.display;
      return mdAndUp && width < COMPACT_WIDTH;
    },
    stacked() {
      return this.$vuetify.display.width < COMPACT_WIDTH;
    },
    datesOnly() {
      const { width, mdAndUp } = this.$vuetify.display;
      return mdAndUp && width < DATES_ONLY_WIDTH;
    },
    hasDates() {
      return !!(this.start || this.end);
    },
    rangeName() {
      return this.$i18n.t(this.rangeObject.name);
    },
  },
  methods: {
    ...mapActions({
      changeDateRangeObject: "changeDateRangeObject",
    }),
  },
  watch: {
    fiscalYearStart: {
      // this ensures that when the fiscal year start changes, the date range is updated
      // throughout the whole application
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          if (oldValue === 0) {
            // this is the first time the fiscal year start is set
            // we need to set the date range to the current + previous fiscal year
            this.changeDateRangeObject(this.defaultFYDateRangeName);
          } else if (newValue === 0 && this.dateRangeName.includes("fy")) {
            // when the fiscal year start is set to 0, we will hide the
            // FY related date ranges, so we need to select the default
            this.changeDateRangeObject(this.defaultDateRangeName);
          } else {
            // in all other cases, we just make sure the date range is updated
            // by refreshing the current date range
            this.changeDateRangeObject(this.dateRangeName);
          }
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped lang="scss">
// not `text-medium-emphasis` - it forces on-background color, which is unreadable
// on the blue nav-bar where the widget lives on mobile; keep the inherited color
.date-range__dates {
  opacity: var(--v-medium-emphasis-opacity);
}

.date-range--stacked :deep(.v-field.v-field--variant-underlined) {
  .v-field__prepend-inner,
  .v-field__append-inner {
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }
}

.date-range--stacked
  :deep(
    .v-field.v-field--variant-underlined.v-field--no-label .v-field__input
  ) {
  padding-top: 4px;
  padding-bottom: 4px;
}
</style>
